import { get } from "lodash";
import * as xlsx from "xlsx";
import { IColumn } from "../interfaces/table";
import {
  IPagedPersonsQuery,
  PagedPersonsQueryField,
} from "../interfaces/personer";
import { getActiveColumnFields } from "./table";
import axios from "axios";
import { defaultApiUrl, axiosConfig } from "../api/util";
import { IPagedFirmaQuery, PagedFirmaQueryField } from "../interfaces/firma";

/** Matches DownloadListeController pageSize; keeps GraphQL cost within MaxTypeCost. */
const DOWNLOAD_GRAPHQL_FIRST = 100;
import { IPagedKursusQuery, PagedKursusQueryField } from "../interfaces/kursus";
import { getFields } from "./graphqlUtil";
import { PagedLinjeQuery, PagedLinjeQueryField } from "../interfaces/linje";

/**
 * Helper function to format data for excel and csv export
 * @param columns the IColumn type for the table.
 * @param data the corresponding data array.
 * @param additionalData an optional additional data object, this will be assigned to the data object
 * @returns an object with the column headers as keys and data mapped to its values
 */
function formatData<T>(
  columns: IColumn<T>[],
  data: any[],
  additionalData?: Record<string, any>,
) {
  // get active columns
  const headerTypes = columns.filter((col) => col.active);
  const headers = headerTypes.map((col) => col.title);

  // "re-shape" data to fit columns
  const formattedData = data.map((d) => {
    return headers.reduce((acc, value) => {
      return {
        ...acc,
        [value]: get(
          d,
          headerTypes.find((type) => {
            return type.title === value;
          })?.field as unknown as string,
        ),
      };
    }, {});
  });

  if (additionalData) {
    formattedData.forEach((element) => {
      Object.assign(element, additionalData);
    });
  }

  return formattedData;
}
/**
 * Creates and downloads an XLSX file with the provided data and column headers.
 * @param columns the IColumn type for the table.
 * @param tableData the corresponding data array.
 * @param fileName optional filename.
 * @param additionalData an additional data object, this will be assigned to the table data object
 */
export function tableToXLSX<T>(
  columns: IColumn<T>[],
  tableData: any[],
  options?: {
    fileName?: string;
    additionalData?: Record<string, any>;
  },
) {
  // format data
  const rows = formatData(columns, tableData, options?.additionalData);

  // build excel stuff
  const ws = xlsx.utils.json_to_sheet(rows);
  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, "Data");
  // download file
  xlsx.writeFile(wb, `${options?.fileName ?? "data"}.xlsx`, {
    compression: true,
    ignoreEC: false,
  });
}

export async function downloadIndexTableToExcel(
  ids: string[],
  query:
    IPagedPersonsQuery | IPagedFirmaQuery | IPagedKursusQuery | PagedLinjeQuery,
  type: "personer" | "firmaer" | "kursusholds" | "fmulinjer",
  columns: IColumn<
    | PagedPersonsQueryField
    | PagedFirmaQueryField
    | PagedKursusQueryField
    | PagedLinjeQueryField
  >[],
  fileName?: string,
) {
  const gqlFields = getFields(getActiveColumnFields(columns));
  const filters = query.filters;
  const gql = `query downloadExcel { ${type}(${
    ids.length ? `first: ${DOWNLOAD_GRAPHQL_FIRST}` : ""
  }, ${filters}) { edges { node { ${gqlFields} } } } }`;

  const mappedColumns = getActiveColumnFields(columns)?.reduce(
    (acc, field) => ({
      ...acc,
      [columns.find((col) => col.field === field)?.title!]: field.split("."),
    }),
    {},
  );

  const body = {
    ids: ids,
    gqlQuery: JSON.stringify({ query: gql }),
    headerToLocationMap: mappedColumns,
  };

  try {
    const response = await axios.post(`${defaultApiUrl}${"download"}`, body, {
      ...axiosConfig,
      responseType: "blob",
    });

    const ref = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.setAttribute("href", ref);
    link.setAttribute("download", `${fileName ?? `${type}-export`}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error: any) {
    return Promise.reject(error.response);
  }
}
